import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Grid,
    IconButton,
    CircularProgress
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { useAlert } from "../alerts/AlertContext";
import AxiosInstance from "../../services/AxiosInstance";
import { IMPORT_URL } from "../../Constants/Constants";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
const initialForm = {
    id: null,
    tableCode: "",
    tableName: "",
    displayName: "",
    description: "",
    enabled: true,
    allowInsert: true,
    allowUpdate: false
};


const ImportTableConfigDialog = ({
    open,
    onClose,
    editData,
    onSuccess
}) => {

    const showAlert = useAlert();
    const [formData, setFormData] =
        useState(initialForm);

    const [errors, setErrors] =
        useState({});

    const [loading, setLoading] =
        useState(false);


    /*
     * Populate form when editing
     */
    useEffect(() => {

        if (open) {

            if (editData) {

                setFormData({
                    id: editData.id || null,
                    tableCode:
                        editData.tableCode || "",
                    tableName:
                        editData.tableName || "",
                    displayName:
                        editData.displayName || "",
                    description:
                        editData.description || "",
                    enabled:
                        editData.enabled ?? true,
                    allowInsert:
                        editData.allowInsert ?? true,
                    allowUpdate:
                        editData.allowUpdate ?? false
                });

            } else {

                setFormData(
                    initialForm
                );
            }

            setErrors({});
        }

    }, [open, editData]);


    /*
     * Handle text fields
     */
    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };


    /*
     * Handle switches
     */
    const handleSwitchChange = (event) => {

        const {
            name,
            checked
        } = event.target;

        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: checked
            };
            return updated;
        }
        );
    };


    /*
     * Validate form
     */
    const validateForm = () => {

        const newErrors = {};

        if (!formData.tableCode.trim()) {

            newErrors.tableCode =
                "Table Code is required.";
        }

        if (!formData.tableName.trim()) {

            newErrors.tableName =
                "Table Name is required.";
        }

        if (!formData.displayName.trim()) {

            newErrors.displayName =
                "Display Name is required.";
        }

        setErrors(newErrors);

        return (
            Object.keys(newErrors).length === 0
        );
    };


    /*
     * Save
     */
    const handleSave = async () => {

        if (!validateForm()) {
            return;
        }

        try {

            setLoading(true);

            const payload = {
                tableCode:
                    formData.tableCode.trim(),

                tableName:
                    formData.tableName.trim(),

                displayName:
                    formData.displayName.trim(),

                description:
                    formData.description.trim(),

                enabled:
                    formData.enabled,

                allowInsert:
                    formData.allowInsert,

                allowUpdate:
                    formData.allowUpdate
            };


            let response;

            /*
             * Update
             */
            if (formData.id) {

                response =
                    await AxiosInstance.put(
                        `${IMPORT_URL}/table/${formData.id}`,
                        payload
                    );

            }

            /*
             * Insert
             */
            else {

                response =
                    await AxiosInstance.post(
                        `${IMPORT_URL}/table`,
                        payload
                    );
            }


            showAlert(response.data?.message ||
                (
                    formData.id
                        ? "Import table configuration updated successfully."
                        : "Import table configuration created successfully."
                ), 'success')


            if (onSuccess) {
                onSuccess(response.data);
            }

            handleClose();

        } catch (error) {

            console.error(
                "Unable to save import table configuration",
                error
            );

            const message =
                error.response?.data?.message ||
                "Unable to save import table configuration.";

            showAlert(message, 'error');

        } finally {

            setLoading(false);
        }
    };


    /*
     * Close dialog
     */
    const handleClose = () => {

        if (loading) {
            return;
        }

        setFormData(initialForm);
        setErrors({});

        onClose();
    };


    return (

        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >

                {formData.id
                    ? "Edit Import Table Configuration"
                    : "Add Import Table Configuration"
                }

                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                >
                    <CloseIcon />
                </IconButton>

            </DialogTitle>


            <DialogContent dividers>
                <ThemeProvider>
                    <Grid
                        container
                        spacing={2}
                        sx={{ mt: 0.5 }}
                    >

                        {/* Table Code */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >

                            <TextField
                                fullWidth
                                label="Table Code"
                                name="tableCode"
                                value={
                                    formData.tableCode
                                }
                                onChange={
                                    handleChange
                                }
                                error={
                                    Boolean(
                                        errors.tableCode
                                    )
                                }
                                helperText={
                                    errors.tableCode
                                }
                                disabled={Boolean(
                                    formData.id
                                )}
                                required
                            />

                        </Grid>


                        {/* Table Name */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >

                            <TextField
                                fullWidth
                                label="Table Name"
                                name="tableName"
                                value={
                                    formData.tableName || ''
                                }
                                onChange={
                                    handleChange
                                }
                                error={
                                    Boolean(
                                        errors.tableName
                                    )
                                }
                                helperText={
                                    errors.tableName
                                }
                                disabled={Boolean(
                                    formData.id
                                )}
                                required

                            />

                        </Grid>


                        {/* Display Name */}

                        <Grid
                            item
                            xs={12}
                        >

                            <TextField
                                fullWidth
                                label="Display Name"
                                name="displayName"
                                value={
                                    formData.displayName
                                }
                                onChange={
                                    handleChange
                                }
                                error={
                                    Boolean(
                                        errors.displayName
                                    )
                                }
                                helperText={
                                    errors.displayName
                                }
                                required
                            />

                        </Grid>


                        {/* Description */}

                        <Grid
                            item
                            xs={12}
                        >

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                name="description"
                                value={
                                    formData.description || ''
                                }
                                onChange={
                                    handleChange
                                }


                            />

                        </Grid>


                        {/* Enabled */}

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            formData.enabled
                                        }
                                        onChange={
                                            handleSwitchChange
                                        }
                                        name="enabled"
                                    />
                                }
                                label="Enabled"
                            />

                        </Grid>


                        {/* Allow Insert */}

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            formData.allowInsert
                                        }
                                        onChange={
                                            handleSwitchChange
                                        }
                                        name="allowInsert"
                                    />
                                }
                                label="Allow Insert"
                            />

                        </Grid>


                        {/* Allow Update */}

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            formData.allowUpdate
                                        }
                                        onChange={
                                            handleSwitchChange
                                        }
                                        name="allowUpdate"
                                    />
                                }
                                label="Allow Update"
                            />

                        </Grid>

                    </Grid>
                </ThemeProvider>
            </DialogContent>


            <DialogActions sx={{ px: 3, py: 2 }}>

                <Button
                    onClick={handleClose}
                    disabled={loading}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                >

                    {loading && (
                        <CircularProgress
                            size={20}
                            sx={{ mr: 1 }}
                        />
                    )}

                    {formData.id
                        ? "Update"
                        : "Save"
                    }

                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default ImportTableConfigDialog;